import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpLeft } from "lucide-react";
import Footer from "@/components/athena/Footer";

const SECTIONS = [
  {
    heading: "1. Our commitment",
    body: (
      <>
        Athena Nutrascience Pvt. Ltd. (&ldquo;Athena&rdquo;) is committed to
        delivering evidence-based nutraceutical, biotechnology and life-
        sciences products that are safe, effective, and consistent — every
        batch, every market, every time. This Quality Policy defines the
        principles, standards and behaviours that guide every function of
        our organisation, from raw-material sourcing to final release.
      </>
    ),
  },
  {
    heading: "2. Quality principles",
    body: (
      <>
        Our quality culture rests on five non-negotiable principles:
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#B89B64]">
          <li>
            <strong>Evidence first.</strong> No claim, formulation or
            specification enters our line without a defensible dataset.
          </li>
          <li>
            <strong>Traceable by design.</strong> Every batch carries a
            full, auditable chain of custody — from botanical source to
            finished dose.
          </li>
          <li>
            <strong>In-house rigour.</strong> Analytics, potency and purity
            testing are performed under our own supervision, not outsourced
            without oversight.
          </li>
          <li>
            <strong>Continuous improvement.</strong> We treat every
            deviation, complaint or observation as an opportunity to
            strengthen our system.
          </li>
          <li>
            <strong>Compliance is the floor, not the ceiling.</strong> We
            meet regulatory standards as a minimum — internal specifications
            typically exceed them.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: "3. Certifications & standards",
    body: (
      <>
        Athena operates in alignment with the following international and
        Indian quality frameworks:
        <ul className="mt-4 space-y-2 pl-5 list-disc marker:text-[#B89B64]">
          <li>
            <strong>ISO 9001</strong> — Quality Management Systems
          </li>
          <li>
            <strong>ISO 22000</strong> — Food Safety Management Systems
          </li>
          <li>
            <strong>HALAL</strong> — Compliance for global halal markets
          </li>
          <li>
            <strong>KOSHER</strong> — Compliance for kosher-certified
            supply chains
          </li>
        </ul>
        Our manufacturing partners and analytical laboratories operate under
        applicable Good Manufacturing Practice (GMP) frameworks, and all
        products intended for sale carry the certifications required by
        their destination markets.
      </>
    ),
  },
  {
    heading: "4. Product safety & integrity",
    body: (
      <>
        Every product we release is designed with three safety layers in
        mind: raw-material qualification, in-process controls, and finished-
        product release testing. We verify identity, purity, potency,
        microbial load, and stability against internal specifications that
        typically exceed regulatory minimums. Any batch that fails to meet
        specification is quarantined, root-cause investigated, and destroyed
        or rejected before it reaches a customer.
      </>
    ),
  },
  {
    heading: "5. Raw-material sourcing",
    body: (
      <>
        We qualify every supplier before onboarding and audit them on a
        risk-based schedule thereafter. Botanicals, ferment-derived actives,
        excipients and packaging materials must arrive with a valid
        Certificate of Analysis and are re-tested in-house before they enter
        our formulation stream. Adulteration, misidentification, and heavy-
        metal contamination are treated as automatic rejection criteria.
      </>
    ),
  },
  {
    heading: "6. Research & clinical rigour",
    body: (
      <>
        Formulations progress through discovery, characterisation,
        bioavailability engineering and validation stages before they are
        commercialised. Where clinical evidence is claimed, studies are
        designed with independent statistical review, executed under IRB or
        equivalent ethical oversight, and reported honestly regardless of
        outcome. We do not overstate benefits.
      </>
    ),
  },
  {
    heading: "7. People & training",
    body: (
      <>
        Quality is built by people who understand it. Every team member —
        from bench scientist to warehouse operator — is trained on the
        elements of this policy relevant to their role, and refresher
        training is delivered at least annually. Employees are empowered to
        stop the line if quality is at risk.
      </>
    ),
  },
  {
    heading: "8. Complaints & recalls",
    body: (
      <>
        We maintain a documented complaints and recalls procedure. Every
        complaint is logged, investigated, and closed with a formal root-
        cause analysis and corrective/preventive action. Where a batch
        risk is identified, we have the systems and traceability to execute
        a market recall efficiently and transparently.
      </>
    ),
  },
  {
    heading: "9. Continuous improvement",
    body: (
      <>
        Our Quality Management System is reviewed by leadership at least
        annually. Metrics — deviation rate, complaint rate, on-time-in-full
        release, audit findings — drive the objectives set for each new
        cycle. This is not a certificate we frame; it is a discipline we
        practice.
      </>
    ),
  },
  {
    heading: "10. Governance",
    body: (
      <>
        Ultimate accountability for this policy rests with the Founders of
        Athena Nutrascience. Day-to-day authority is delegated to the Head
        of Quality, who reports directly to the CEO. This policy is
        reviewed and republished at least annually or whenever material
        changes to standards or operations require it.
      </>
    ),
  },
  {
    heading: "11. Contact",
    body: (
      <>
        For questions about this policy, quality documentation, or to
        request a supplier audit or Certificate of Analysis, write to{" "}
        <a
          href="mailto:connect@athenanutrascience.com"
          className="link-underline text-[#1A382A] font-medium"
        >
          connect@athenanutrascience.com
        </a>
        .
      </>
    ),
  },
];

export default function QualityPolicy() {
  useEffect(() => {
    document.title = "Quality Policy — Athena Nutrascience";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App bg-[#F8F7F4] min-h-screen">
      {/* Simple top bar */}
      <header className="border-b border-[#D5D1C8] bg-[#F8F7F4]/85 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <Link
            to="/"
            data-testid="quality-nav-home"
            className="flex items-center gap-3 group"
          >
            <span className="w-8 h-8 rounded-full border border-[#1A382A] flex items-center justify-center">
              <span className="w-2 h-2 rounded-full bg-[#1A382A] group-hover:bg-[#B89B64] transition-colors" />
            </span>
            <span className="font-display text-2xl tracking-tight text-[#0A1713] leading-none">
              Athena<span className="text-[#B89B64]">.</span>
            </span>
          </Link>
          <Link
            to="/"
            data-testid="quality-back-link"
            className="flex items-center gap-2 label-mono hover:text-[#1A382A] transition-colors"
          >
            <ArrowUpLeft size={14} strokeWidth={1.5} /> BACK TO HOME
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-10">
        <span className="label-mono">// LEGAL · QUALITY POLICY</span>
        <h1
          data-testid="quality-title"
          className="font-display text-4xl sm:text-5xl md:text-6xl mt-6 leading-[1.02] tracking-tight text-[#0A1713]"
        >
          The standards
          <br />
          we live <em className="italic text-[#1A382A]">by</em>.
        </h1>
        <p className="mt-6 max-w-2xl text-[#3D4F46] leading-relaxed">
          Quality is not a department at Athena — it is the operating system
          for the whole company. This policy documents the principles,
          certifications and behaviours that make our products worthy of the
          claims they carry.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          {["ISO 9001", "ISO 22000", "HALAL", "KOSHER"].map((c) => (
            <span
              key={c}
              className="label-mono text-[#1A382A] border border-[#D5D1C8] bg-[#EAE7E0] px-3 py-2"
            >
              {c}
            </span>
          ))}
        </div>
        <div className="mt-6 label-mono text-[#3D4F46]">
          Last updated: December 2025
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1100px] mx-auto px-6 md:px-10 pb-20 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-16">
          {/* Sticky mini-TOC */}
          <aside className="lg:col-span-1 lg:sticky lg:top-32 lg:self-start">
            <div className="label-mono mb-6">/ ON THIS PAGE</div>
            <ol className="space-y-2 text-sm">
              {SECTIONS.map((s) => {
                const id = s.heading.split(" ")[0].replace(".", "");
                return (
                  <li key={s.heading}>
                    <a
                      href={`#q${id}`}
                      className="text-[#3D4F46] hover:text-[#1A382A] transition-colors block"
                    >
                      {s.heading}
                    </a>
                  </li>
                );
              })}
            </ol>
          </aside>

          <article className="lg:col-span-2 space-y-12">
            {SECTIONS.map((s) => {
              const id = s.heading.split(" ")[0].replace(".", "");
              return (
                <div
                  key={s.heading}
                  id={`q${id}`}
                  className="scroll-mt-32 border-t border-[#D5D1C8] pt-8 first:border-t-0 first:pt-0"
                >
                  <h2 className="font-display text-2xl md:text-3xl text-[#0A1713] leading-tight">
                    {s.heading}
                  </h2>
                  <div className="mt-4 text-[#3D4F46] leading-relaxed text-base">
                    {s.body}
                  </div>
                </div>
              );
            })}
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
